'use client';

import React, { useState } from 'react';
import { Bell, ChevronDown, MapPin } from 'lucide-react';


interface TopbarProps {
  title?: string;
  subtitle?: string;
  role?: 'supplier' | 'retailer' | 'hybrid';
}

const notifications = [
  { id: 'notif-1', text: '5 lots expirent dans 7 jours', type: 'danger', time: 'Il y a 12 min' },
  { id: 'notif-2', text: 'Nouvelle commande de Snack Atlas', type: 'info', time: 'Il y a 28 min' },
  { id: 'notif-3', text: 'Paiement reçu — 1 850 MAD', type: 'success', time: 'Il y a 1h' },
  { id: 'notif-4', text: 'Limite crédit atteinte — Café Bab Doukkala', type: 'warning', time: 'Il y a 2h' },
];

export default function Topbar({ title, subtitle, role = 'hybrid' }: TopbarProps) {
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const roleLabel = role === 'supplier' ? 'Fournisseur' : role === 'retailer' ? 'Détaillant' : 'Hybride';
  const roleBadgeClass = role === 'hybrid' ? 'status-badge-hybrid' : role === 'supplier' ? 'status-badge-loading' : 'status-badge-active';

  return (
    <header className="h-[var(--topbar-height)] bg-card border-b border-border flex items-center px-4 lg:px-6 gap-4 sticky top-0 z-20">
      {/* Title */}
      <div className="flex-1 min-w-0">
        {title && (
          <div>
            <h1 className="text-base font-700 text-foreground truncate">{title}</h1>
            {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
          </div>
        )}
      </div>

      {/* Zone indicator */}
      <div className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground bg-muted px-2.5 py-1.5 rounded-lg">
        <MapPin size={12} />
        <span>Centre Ville, Marrakech</span>
      </div>

      {/* Notifications */}
      <div className="relative">
        <button
          onClick={() => { setNotifOpen(!notifOpen); setProfileOpen(false); }}
          className="relative w-9 h-9 flex items-center justify-center rounded-lg hover:bg-muted transition-colors"
        >
          <Bell size={18} className="text-muted-foreground" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-danger rounded-full"></span>
        </button>
        {notifOpen && (
          <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-xl shadow-dropdown z-50 overflow-hidden fade-in">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <span className="text-sm font-600 text-foreground">Notifications</span>
              <span className="text-2xs font-600 px-1.5 py-0.5 bg-danger/10 text-danger rounded-full">{notifications.length} nouvelles</span>
            </div>
            <div className="divide-y divide-border">
              {notifications.map((n) => (
                <div key={n.id} className="px-4 py-3 hover:bg-muted transition-colors cursor-pointer">
                  <p className="text-xs text-foreground mb-0.5">{n.text}</p>
                  <p className="text-2xs text-muted-foreground">{n.time}</p>
                </div>
              ))}
            </div>
            <div className="px-4 py-2.5 border-t border-border">
              <button className="text-xs text-primary font-600 hover:underline">Voir toutes les notifications</button>
            </div>
          </div>
        )}
      </div>

      {/* Profile */}
      <div className="relative">
        <button
          onClick={() => { setProfileOpen(!profileOpen); setNotifOpen(false); }}
          className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-muted transition-colors"
        >
          <div className="w-7 h-7 rounded-full gradient-primary flex items-center justify-center text-white text-xs font-700">
            KA
          </div>
          <div className="hidden md:block text-left">
            <p className="text-xs font-600 text-foreground">Karim Alaoui</p>
            <p className="text-2xs text-muted-foreground">+212 661-234567</p>
          </div>
          <ChevronDown size={14} className="text-muted-foreground hidden md:block" />
        </button>
        {profileOpen && (
          <div className="absolute right-0 top-full mt-2 w-52 bg-card border border-border rounded-xl shadow-dropdown z-50 overflow-hidden fade-in">
            <div className="px-4 py-3 border-b border-border">
              <p className="text-sm font-600 text-foreground">Karim Alaoui</p>
              <p className="text-xs text-muted-foreground">karim.alaoui@gerant.ma</p>
              <span className={`inline-flex items-center text-2xs font-600 px-1.5 py-0.5 rounded-full mt-1 ${roleBadgeClass}`}>
                {roleLabel}
              </span>
            </div>
            <div className="py-1">
              <button className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors">Mon Profil</button>
              <button className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors">Paramètres</button>
              <button className="w-full text-left px-4 py-2 text-sm text-danger hover:bg-danger/10 transition-colors">Déconnexion</button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}